// The Production step and the Changes tab: the CM's site, what HHL has consigned there for this project (the
// Consignment page's own numbers, summarizeCms()), the project's make orders, and the change notices that touch its
// parts. Links go to Consignment, the Make plan and Specs, where these are worked on. Each list is the usual table
// (DataTable: views, columns, Copy / CSV), the sites as bands of one table.
import { ArrowRight } from "lucide-react";
import { useMemo } from "react";
import type { CmStockLine } from "../../../../shared/consignment.ts";
import { PO_STATUS_LABELS } from "../../../../shared/po.ts";
import type { Readiness } from "../../../../shared/projects.ts";
import { money, qty } from "../../lib/format.ts";
import { Link, navigate, setSearch } from "../../lib/router.tsx";
import { useRows } from "../../lib/sync.ts";
import { Badge, cx } from "../../ui/controls.tsx";
import { type Column, DataTable, type TableSection } from "../../ui/DataTable.tsx";
import { PaneLink, usePaneOpen } from "../../ui/SidePane.tsx";
import { makeOrderStates, STATUS_LABELS, useCms } from "../consignment/lib.ts";
import { TableCard } from "./ProjectsPage.tsx";
import { Card, StepGaps, type TabProps } from "./Workspace.tsx";

/** A part at a CM site, or (`s` missing) the line saying a site has nothing of this project's yet, so its band shows. */
type StockRow = { id: string; site: string; s?: CmStockLine };
type MakeRow = Readiness["makeOrders"][number];

export function ProductionTab({ project, px, scope, r, here }: TabProps) {
  const cms = useCms();
  const states = useMemo(() => makeOrderStates(cms), [cms]);
  const openPane = usePaneOpen();
  const cm = project.cm_vendor_id ? px.vendorById.get(project.cm_vendor_id) : undefined;
  const inScope = (id: string) => scope.parts.has(id) || scope.variantIds.has(id);
  // One band per CM site, in the readiness' order: its name, what this project's parts there are worth, Consignment
  const bands = new Map<string, TableSection>();
  const stockRows: StockRow[] = r.cmSites.flatMap((site, order) => {
    const c = cms.get(site.id);
    const stock = (c?.stock ?? []).filter((s) => inScope(s.itemId) && (s.onHand || s.inTransit));
    const value = stock.reduce((n, s) => n + (s.value ?? 0), 0);
    const noCost = stock.filter((s) => s.value == null).length;
    bands.set(site.id, {
      key: site.id,
      order,
      label: (
        <span role="presentation" onKeyDown={(e) => e.stopPropagation()} className="flex items-end gap-2">
          <span className="text-text">{site.name}</span>
          <span className="font-normal tracking-normal normal-case">
            CM site of {cm ? cm.short_name || cm.name : "the CM"} · {money(Math.round(value))} of this project's parts there
            {noCost ? ` (${noCost} without a cost)` : ""}
          </span>
          <Link to={`/consignment?cm=${site.id}`} className="flex items-center gap-1 font-normal tracking-normal text-accent-text normal-case hover:underline">
            Consignment <ArrowRight className="size-3" />
          </Link>
        </span>
      ),
    });
    return stock.length ? stock.map((s) => ({ id: `${site.id}:${s.itemId}`, site: site.id, s })) : [{ id: `${site.id}:none`, site: site.id }];
  });
  const stockColumns: Column<StockRow>[] = [
    {
      key: "sku",
      header: "SKU",
      width: "minmax(120px,150px)",
      value: (x) => x.s?.item?.sku ?? "",
      cell: (x) => <span className="truncate font-mono text-[12px]">{x.s?.item?.sku}</span>,
    },
    {
      key: "name",
      header: "Part",
      width: "minmax(180px,1fr)",
      value: (x) => x.s?.item?.name ?? "",
      cell: (x) =>
        x.s ? <span className="truncate">{x.s.item?.name}</span> : <span className="text-muted">Nothing of this project's is consigned here yet.</span>,
    },
    {
      key: "count",
      header: "Count",
      headerTitle: "A Shopify item's number is Shopify's, and only Shopify changes it",
      width: "120px",
      value: (x) => (x.s?.inShopify ? "Shopify's count" : ""),
      cell: (x) => (x.s?.inShopify ? <span className="text-[11px] text-faint">Shopify's count</span> : null),
    },
    {
      key: "on_hand",
      header: "On hand",
      width: "90px",
      align: "right",
      value: (x) => x.s?.onHand,
      cell: (x) => x.s && <span className="font-mono text-[12px]">{qty(x.s.onHand)}</span>,
    },
    {
      key: "in_transit",
      header: "In transit",
      width: "90px",
      align: "right",
      value: (x) => x.s?.inTransit,
      cell: (x) => (x.s?.inTransit ? <span className="font-mono text-[12px] text-muted">{qty(x.s.inTransit)}</span> : null),
    },
    {
      key: "free",
      header: "Free",
      width: "90px",
      align: "right",
      value: (x) => x.s?.free,
      cell: (x) => x.s && <span className="font-mono text-[12px]">{qty(x.s.free)}</span>,
    },
    {
      key: "value",
      header: "Value",
      width: "100px",
      align: "right",
      value: (x) => x.s?.value,
      cell: (x) =>
        x.s && <span className="font-mono text-[12px]">{x.s.value == null ? <span className="text-warn">no cost</span> : money(Math.round(x.s.value))}</span>,
    },
  ];

  const makeColumns: Column<MakeRow>[] = [
    {
      key: "po",
      header: "PO",
      width: "110px",
      value: (m) => Number((m.po?.number ?? "").replace(/\D/g, "")) || null,
      text: (m) => m.po?.number ?? "",
      cell: (m) => (
        <PaneLink kind="po" id={m.po?.id ?? ""} to={`/pos?open=${m.po?.id ?? ""}`} className="font-mono text-[12px] hover:underline">
          {m.po?.number ?? "PO"}
        </PaneLink>
      ),
    },
    {
      key: "sku",
      header: "SKU",
      width: "minmax(110px,140px)",
      value: (m) => m.item?.sku ?? "",
      cell: (m) => <span className="truncate font-mono text-[12px]">{m.item?.sku}</span>,
    },
    { key: "name", header: "Item", width: "minmax(160px,1fr)", value: (m) => m.item?.name ?? "" },
    {
      key: "status",
      header: "PO status",
      width: "130px",
      value: (m) => (m.po ? PO_STATUS_LABELS[m.po.status] : ""),
      cell: (m) => (m.po ? <Badge tone={m.open ? "accent" : "neutral"}>{PO_STATUS_LABELS[m.po.status]}</Badge> : null),
    },
    {
      key: "build",
      header: "Build",
      width: "minmax(170px,0.8fr)",
      value: (m) => {
        const s = states.get(m.id);
        return s ? `${STATUS_LABELS[s.status]} · ${qty(s.output)} of ${qty(s.qty)} built` : "";
      },
      cell: (m) => {
        const s = states.get(m.id);
        return (
          <span className={cx("truncate text-[12px]", s?.status === "short" ? "text-warn" : "text-muted")}>
            {s ? `${STATUS_LABELS[s.status]} · ${qty(s.output)} of ${qty(s.qty)} built` : ""}
          </span>
        );
      },
    },
  ];

  return (
    <div className="mx-auto flex max-w-6xl flex-col gap-3 p-4">
      <StepGaps r={r} steps={["production"]} project={project} px={px} here={here} />
      {!r.cmSites.length ? (
        <Card title="CM site">
          <div className="px-4 py-3 text-muted">
            {cm ? `${cm.short_name || cm.name} has no CM site yet. ` : "Choose the project's contract manufacturer (Basics) first. "}
            <Link to="/consignment?setup=new" className="text-accent-text hover:underline">
              Set up a CM site
            </Link>
          </div>
        </Card>
      ) : (
        <TableCard title="Consigned at the CM">
          {(slot) => (
            <DataTable
              fit
              rows={stockRows}
              columns={stockColumns}
              sectionOf={(x) => bands.get(x.site) ?? null}
              onOpen={(x) => x.s && setSearch({ open: x.s.itemId })}
              exportName="CM stock"
              viewsSlot={slot}
              pageFilters={false}
              rowNoun={["part", "parts"]}
            />
          )}
        </TableCard>
      )}
      <TableCard
        title="Make orders"
        note="Builds of the project's products and sub-assemblies at a CM"
        actions={
          <Link to={`/make?project=${project.id}`} className="flex items-center gap-1 text-[12px] text-accent-text hover:underline">
            Make plan <ArrowRight className="size-3" />
          </Link>
        }
      >
        {(slot) => (
          <DataTable
            fit
            rows={r.makeOrders}
            columns={makeColumns}
            onOpen={(m) => m.po && openPane("po", m.po.id)}
            exportName="Make orders"
            viewsSlot={slot}
            pageFilters={false}
            rowNoun={["make order", "make orders"]}
            empty={
              <div className="px-4 py-3 text-muted">
                No make order yet. A pilot build starts from the{" "}
                <Link to={`/make?project=${project.id}`} className="text-accent-text hover:underline">
                  Make plan
                </Link>
                .
              </div>
            }
          />
        )}
      </TableCard>
    </div>
  );
}

type NoticeRow = Readiness["notices"][number];

export function ChangesTab({ scope, r }: TabProps) {
  const links = useRows("change_notice_item");
  const items = useRows("item");
  const skuOf = useMemo(() => new Map(items.map((i) => [i.id, i.sku || i.name])), [items]);
  const inScope = (id: string) => scope.parts.has(id) || scope.variantIds.has(id);
  const touched = (n: NoticeRow) =>
    links
      .filter((l) => l.change_notice_id === n.id && inScope(l.item_id))
      .map((l) => skuOf.get(l.item_id) ?? "")
      .join(", ");
  const effective = (n: NoticeRow) => (n.effective === "date" ? `from ${n.effective_date ?? "?"}` : "from the next PO");
  const columns: Column<NoticeRow>[] = [
    {
      key: "number",
      header: "No.",
      width: "90px",
      value: (n) => Number(String(n.number).replace(/\D/g, "")) || null,
      text: (n) => n.number,
      cell: (n) => <span className="font-mono text-[12px]">{n.number}</span>,
    },
    { key: "title", header: "Title", width: "minmax(200px,1fr)" },
    {
      key: "status",
      header: "Status",
      width: "100px",
      value: (n) => (n.status === "released" ? "Released" : "Draft"),
      cell: (n) => <Badge tone={n.status === "released" ? "ok" : "accent"}>{n.status === "released" ? "Released" : "Draft"}</Badge>,
    },
    { key: "effective", header: "Effective", width: "150px", value: effective, cell: (n) => <span className="text-[12px] text-muted">{effective(n)}</span> },
    {
      key: "touches",
      header: "Touches",
      headerTitle: "The project's parts and variants it changes",
      width: "minmax(160px,1fr)",
      value: touched,
      cell: (n) => (
        <span className="truncate font-mono text-[11px] text-muted" title={touched(n)}>
          {touched(n)}
        </span>
      ),
    },
  ];
  return (
    <div className="mx-auto flex max-w-6xl flex-col gap-3 p-4">
      <TableCard
        title="Change notices"
        note="Every change notice that touches one of the project's parts or variants"
        actions={
          <Link to="/specs?tab=ecn" className="flex items-center gap-1 text-[12px] text-accent-text hover:underline">
            Specs <ArrowRight className="size-3" />
          </Link>
        }
      >
        {(slot) => (
          <DataTable
            fit
            rows={r.notices}
            columns={columns}
            // Newest first, as readiness lists them
            defaultSort={{ key: "number", desc: true }}
            onOpen={(n) => navigate(`/specs?tab=ecn&open=${n.id}`)}
            exportName="Change notices"
            viewsSlot={slot}
            pageFilters={false}
            rowNoun={["change notice", "change notices"]}
            empty="No change notice touches this project's parts yet."
          />
        )}
      </TableCard>
    </div>
  );
}
